/**
 * Add User Student ID intake (pure — keep in sync with backend unixIdentity).
 *
 * Spec 4.1: [Mode][Center?][Year][Program][Legacy]
 *   Mode   OF = Offline, ON = Online  (not O / N — N is Noida)
 *   Center Offline only: N Noida, B Bangalore, M Mysuru, C Chennai, R Roorkee
 */

export const UNIX_IDENTITY_FLAG = 'students.unix_identity';

export const STUDENT_ID_INTAKE_MODES = Object.freeze({
  GENERATE: 'generate',
  EXISTING: 'existing',
  LEAD: 'lead',
});

export const MODE_ONLINE = 'ON';
export const MODE_OFFLINE = 'OF';
export const DEFAULT_PROGRAM = 'PO';
export const OFFLINE_CENTER_LETTERS = 'NBMCR';

export function requiresTypedStudentId(flagOn, mode) {
  if (!flagOn) return true;
  return mode === STUDENT_ID_INTAKE_MODES.EXISTING;
}

export function showsTypedStudentId(flagOn, mode) {
  if (!flagOn) return true;
  return mode === STUDENT_ID_INTAKE_MODES.EXISTING || mode === STUDENT_ID_INTAKE_MODES.LEAD;
}

const CATEGORY_TYPE_TO_PROGRAM = Object.freeze({
  PO: 'PO',
  WS: 'WS',
  TI: 'TI',
  UP: 'UP',
  TS: 'TS',
  CG: 'CG',
  SLP: 'SL',
  JOU: 'JO',
  WEBINAR: 'WE',
});

/** First two letters of a label — keep in sync with backend studentIdFormat. */
export function programFromLabel(value) {
  const letters = String(value || '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '');
  if (letters.length >= 2) return letters.slice(0, 2);
  return DEFAULT_PROGRAM;
}

/** Two-letter program from course category type (PO, WS, …). */
export function programFromCategoryType(categoryType, categoryCode) {
  const typeKey = String(categoryType || '')
    .trim()
    .toUpperCase();
  if (CATEGORY_TYPE_TO_PROGRAM[typeKey]) return CATEGORY_TYPE_TO_PROGRAM[typeKey];
  const codeKey = String(categoryCode || '')
    .trim()
    .toUpperCase();
  if (CATEGORY_TYPE_TO_PROGRAM[codeKey]) return CATEGORY_TYPE_TO_PROGRAM[codeKey];
  if (typeKey) return programFromLabel(typeKey);
  if (codeKey) return programFromLabel(codeKey);
  return DEFAULT_PROGRAM;
}

export function modeCodeFromValue(mode) {
  const normalized = String(mode || '')
    .trim()
    .toLowerCase();
  if (normalized === 'offline') return MODE_OFFLINE;
  if (normalized === 'online') return MODE_ONLINE;
  return '';
}

export function normalizeCenterLetter(center) {
  const raw = String(center || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, '');
  if (!raw || raw === 'XX') return '';
  if (raw.startsWith('NOIDA') || raw === 'NO') return 'N';
  if (raw.startsWith('BANGAL') || raw.startsWith('BENGAL') || raw === 'BA' || raw === 'BE') return 'B';
  if (raw.startsWith('MYSUR') || raw === 'MY') return 'M';
  if (raw.startsWith('CHENNAI') || raw === 'CH') return 'C';
  if (raw.startsWith('ROORKEE') || raw === 'RO') return 'R';
  const letter = raw.charAt(0);
  return OFFLINE_CENTER_LETTERS.includes(letter) ? letter : '';
}

export function centerLetterFromValue(center, mode) {
  if (modeCodeFromValue(mode) !== MODE_OFFLINE) return '';
  return normalizeCenterLetter(center);
}

/** @deprecated Use centerLetterFromValue — one letter, not two. */
export function centerCodeFromValue(center, mode) {
  return centerLetterFromValue(center, mode);
}

export function referralLabelFromHeardFrom(options, heardFromId) {
  const id = String(heardFromId || '').trim();
  if (!id) return '';
  const match = (options || []).find((option) => {
    const value = typeof option === 'object' ? option.id : option;
    return String(value) === id;
  });
  if (!match) return '';
  if (typeof match === 'object') {
    return String(match.value || match.label || match.name || '').trim();
  }
  return String(match).trim();
}

export function categoryTypeForStudentId(category) {
  if (!category || typeof category !== 'object') return '';
  return category.categoryType || category.categoryCode || category.code || '';
}

export function studentIdPrefixFor({ mode, center = '', year, program = DEFAULT_PROGRAM } = {}) {
  const yy = String(year || '').padStart(2, '0').slice(-2);
  const prog = String(program || '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, 2);
  if (prog.length !== 2 || !/^\d{2}$/.test(yy)) return '';
  if (mode === MODE_ONLINE) return `${MODE_ONLINE}${yy}${prog}`;
  if (mode === MODE_OFFLINE) {
    const letter = normalizeCenterLetter(center);
    return `${MODE_OFFLINE}${letter}${yy}${prog}`;
  }
  return '';
}

/**
 * Prefix preview for Generate (sequence is assigned on save).
 */
export function generatedStudentIdHint({
  categoryType,
  categoryCode,
  modeOfCourse,
  locationOfCenter,
  now = new Date(),
} = {}) {
  const year = String(now.getFullYear()).slice(-2).padStart(2, '0');
  const program = programFromCategoryType(categoryType, categoryCode);
  const mode = modeCodeFromValue(modeOfCourse);
  const center = centerLetterFromValue(locationOfCenter, modeOfCourse);
  const prefix = studentIdPrefixFor({ mode, center, year, program });
  return {
    prefix,
    program,
    mode,
    center,
    year,
    hasCategory: Boolean(String(categoryType || categoryCode || '').trim()),
    example: prefix ? `${prefix}0001` : '',
  };
}
